import { useQuery } from '@tanstack/react-query';
import { organisationsMetricsAPI } from '../../services/api/organisations/metrics';
import { projectsAPI, streamsAPI, repositoryAPI } from '../../services/api';
import { orgKeys, projectPageKeys, streamPageKeys, repoKeys } from '../../query/queryKeys';

export type MetricsCardFetchScope = {
  orgId: string;
  repositoryId?: string;
  projectId?: string;
  streamId?: string;
};

function cycleTimeQueryKey(scope: MetricsCardFetchScope, windowDays: number) {
  if (scope.projectId && scope.streamId) {
    return projectPageKeys.cycleTime(scope.orgId, scope.streamId, scope.projectId, windowDays);
  }
  if (scope.streamId) {
    return streamPageKeys.cycleTime(scope.orgId, scope.streamId, windowDays);
  }
  if (scope.repositoryId && !scope.streamId && !scope.projectId) {
    return repoKeys.cycleTime(scope.repositoryId, windowDays);
  }
  return orgKeys.cycleTime(scope.orgId, windowDays, scope.repositoryId ?? '');
}

function codeCompositionQueryKey(scope: MetricsCardFetchScope, windowDays: number) {
  if (scope.projectId && scope.streamId) {
    return projectPageKeys.codeComposition(scope.orgId, scope.streamId, scope.projectId, windowDays);
  }
  if (scope.streamId) {
    return streamPageKeys.codeComposition(scope.orgId, scope.streamId, windowDays);
  }
  if (scope.repositoryId && !scope.streamId && !scope.projectId) {
    return repoKeys.codeComposition(scope.repositoryId, windowDays);
  }
  return orgKeys.codeComposition(scope.orgId, windowDays, {
    repositoryId: scope.repositoryId ?? '',
    projectId: scope.projectId ?? '',
    streamId: scope.streamId ?? '',
  });
}

async function fetchCycleTime(scope: MetricsCardFetchScope, windowDays: number) {
  if (scope.projectId && scope.streamId) {
    return projectsAPI.getCycleTime(scope.orgId, scope.streamId, scope.projectId, { windowDays });
  }
  if (scope.streamId) {
    return streamsAPI.getCycleTime(scope.orgId, scope.streamId, { windowDays });
  }
  if (scope.repositoryId && !scope.streamId && !scope.projectId) {
    return repositoryAPI.getCycleTime(scope.repositoryId, { windowDays });
  }
  return organisationsMetricsAPI.getCycleTime(scope.orgId, { windowDays, repositoryId: scope.repositoryId });
}

async function fetchCodeComposition(scope: MetricsCardFetchScope, windowDays: number, period?: 'weekly' | 'monthly') {
  return organisationsMetricsAPI.getCodeComposition(scope.orgId, {
    windowDays,
    repositoryId: scope.repositoryId,
    projectId: scope.projectId,
    streamId: scope.streamId,
    ...(period ? { period } : {}),
  });
}

export function useCycleTimeQuery(
  scope: MetricsCardFetchScope | undefined,
  windowDays: number,
  options: { cycleTimeConfig?: unknown } = {}
) {
  const baseKey = scope ? cycleTimeQueryKey(scope, windowDays) : ['cycleTime', 'disabled'];
  const queryKey =
    scope?.repositoryId && !scope.streamId && !scope.projectId
      ? [...baseKey, options.cycleTimeConfig ?? null]
      : baseKey;

  return useQuery({
    queryKey,
    queryFn: () => fetchCycleTime(scope!, windowDays),
    enabled: Boolean(scope?.orgId),
    staleTime: 60_000,
  });
}

export function useCodeCompositionQuery(
  scope: MetricsCardFetchScope | undefined,
  windowDays: number,
  options: { period?: 'weekly' | 'monthly' } = {}
) {
  const { period } = options;
  return useQuery({
    queryKey: scope
      ? [
          ...codeCompositionQueryKey(scope, windowDays),
          ...(period ? ['byPeriod', period] : ['aggregate']),
        ]
      : ['codeComposition', 'disabled'],
    queryFn: () => fetchCodeComposition(scope!, windowDays, period),
    enabled: Boolean(scope?.orgId),
    staleTime: 60_000,
  });
}

export { cycleTimeQueryKey, codeCompositionQueryKey };
